'use client';

import React, { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage, DotSpinner, useModal } from 'components/shared';
import { PerformanceLabModalBodyScrollLock } from './PerformanceLabModalBodyScrollLock';
import { DiagnosticLearnMoreModal } from './DiagnosticLearnMoreModal';
import { PlansCompareModal } from './PlansCompareModal';
import {
  LANDING_COMPARE_DATA,
  LANDING_COMPARE_HEADERS,
  LANDING_RECOVERY_COMPARE_DATA,
  LANDING_RECOVERY_COMPARE_HEADERS,
  type PlansSectionVariant,
} from './plansSectionData';
import type {
  PerformanceLabPlan,
  PlanIncludeDetail,
  PlanIncludeItem,
} from './buy/india/types';
import { getActivePlanOffer } from './buy/india/planOffer';
import {
  useHandleBookNow,
  useHandlePlanSelect,
  usePerformanceLabCTALabel,
  usePerformanceLabAffiliateDiscount,
} from './hooks';

import { AnimatedGradientBorder } from './AnimatedGradientBorder';
import { PL_COLOR_PRIMARY } from './atoms';
import styles from './LandingPlanCard.module.css';

const BASIC_PLAN_ID = 'diagnostics-basic';
const PRO_PLAN_ID = 'diagnostics-pro';
const ELITE_PLAN_ID = 'diagnostics-elite';

interface LandingPlanCardProps {
  plan: PerformanceLabPlan;
  featured?: boolean;
  variant?: PlansSectionVariant;
  onViewJourney?: () => void;
  onSignUpClick?: () => void;
  pricingTier?: 'programOnly' | 'withMedication';
}

const FEATURED_CARD_RADIUS_PX = 16;

const PlanCheckIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M18.375 10.7188L12.3594 17.2813L9.625 14.2983"
      stroke={PL_COLOR_PRIMARY}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CompareArrow = () => (
  <span className={styles.compareArrowWrap} aria-hidden>
    <svg width="100%" height="100%" viewBox="0 0 14 14" fill="none">
      <path
        d="M2.5 7H11.5M11.5 7L7.5 3M11.5 7L7.5 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const getLabel = (item: string | PlanIncludeItem): string =>
  typeof item === 'string' ? item : item.label;

const getTag = (item: string | PlanIncludeItem): string | undefined =>
  typeof item === 'string' ? undefined : item.tag;

const getDetail = (item: string | PlanIncludeItem): PlanIncludeDetail | undefined =>
  typeof item === 'string' ? undefined : item.detail;

const renderGlpIncludeTagLabel = (
  tagText: string,
  isCgmTag: boolean,
): React.ReactNode => {
  if (!isCgmTag) return tagText;

  const match = tagText.match(/^(.*)\s+x(\d+)$/i);
  if (!match) return tagText;

  return (
    <>
      {`${match[1].trimEnd()} `}
      <span className={styles.glpIncludeTagQuantity}>{`x${match[2]}`}</span>
    </>
  );
};

export const LandingPlanCard: React.FC<LandingPlanCardProps> = ({
  plan,
  featured = false,
  variant = 'diagnostics',
  onViewJourney,
  onSignUpClick,
  pricingTier = 'programOnly',
}) => {
  const t = useTranslations('diagnostics');
  const tLab = useTranslations('performance-lab');
  const modal = useModal();
  const isRecovery = variant === 'recovery';
  const isGlp = variant === 'glp';
  const activeOffer = !isGlp ? getActivePlanOffer(plan) : null;
  const translatePlan = isGlp ? tLab : t;

  // Provider-free hook, so a landing card prices correctly wherever it renders.
  // Without this the card showed the full price while `useHandlePlanSelect`
  // quietly applied the affiliate code at checkout — shown != charged.
  const { getPlanDiscount, getDiscountedPrice } =
    usePerformanceLabAffiliateDiscount();
  const affiliateDiscount = getPlanDiscount(plan.shopifyVariantId);
  const hasAffiliateDiscount =
    !activeOffer && affiliateDiscount.percentage > 0 && plan.price > 0;
  const discountedPrice = getDiscountedPrice(plan.price, plan.shopifyVariantId);
  const handleBookNow = useHandleBookNow(
    isRecovery ? { category: 'recovery' } : undefined,
  );
  const { handlePlanSelect, isAdding } = useHandlePlanSelect(plan, {
    category: isRecovery ? 'recovery' : 'diagnostics',
  });
  const onChoosePlan = useCallback(async () => {
    await handlePlanSelect();
  }, [handlePlanSelect]);

  const i18nPrefix = isRecovery ? 'recoveryPage.plansSection' : 'plansSection';
  const planName = translatePlan(plan.name);
  const ctaLabel = usePerformanceLabCTALabel(
    `${i18nPrefix}.includeDetailCta`,
    'Explore plans',
  );
  const chooseLabel =
    (isRecovery || isGlp) && plan.buttonLabel
      ? translatePlan(plan.buttonLabel)
      : tLab(`${i18nPrefix}.choosePlan`, { plan: planName });

  const isBasicPlan = !isRecovery && !isGlp && plan.id === BASIC_PLAN_ID;
  const isProPlan = !isRecovery && !isGlp && plan.id === PRO_PLAN_ID;
  const isElitePlan = !isRecovery && !isGlp && plan.id === ELITE_PLAN_ID;
  const compareHeaders = isRecovery
    ? LANDING_RECOVERY_COMPARE_HEADERS
    : LANDING_COMPARE_HEADERS;
  const compareData = isRecovery
    ? LANDING_RECOVERY_COMPARE_DATA
    : LANDING_COMPARE_DATA;
  const hasCompareData =
    compareHeaders.length > 0 && compareData.length > 0;

  const openIncludeDetail = useCallback(
    (labelKey: string, detail: PlanIncludeDetail) => {
      const label = t(labelKey);
      const sections =
        detail.sections?.map((section) => ({
          title: section.title,
          content: section.content ?? '',
        })) ?? [];

      modal.set(
        <PerformanceLabModalBodyScrollLock>
          <DiagnosticLearnMoreModal
            title={label}
            modalData={{
              description: detail.description,
              benefits: detail.benefits,
              sectionImageSrc: detail.imageUrl,
              thumbnailSrc: detail.thumbnailUrl ?? detail.imageUrl,
              sections,
              ctaText: ctaLabel,
            }}
            onClose={() => {
              modal.hide();
              modal.clear();
            }}
            onCtaClick={() => {
              modal.hide();
              modal.clear();
              handleBookNow();
            }}
          />
        </PerformanceLabModalBodyScrollLock>,
      );
      modal.setFullView();
      modal.setCloseBtnHidden(true);
      modal.setTop();
      modal.setAlignRight();
      modal.setPortal(true);
      modal.show();
    },
    [ctaLabel, handleBookNow, modal, t],
  );

  const openCompareModal = useCallback(() => {
    if (!hasCompareData) return;

    modal.set(
      <PerformanceLabModalBodyScrollLock>
        <PlansCompareModal
          headers={compareHeaders}
          categories={compareData}
          compareOrigin={isRecovery ? 'recovery' : undefined}
          onClose={() => {
            modal.hide();
            modal.clear();
          }}
        />
      </PerformanceLabModalBodyScrollLock>,
    );
    modal.setCloseBtnHidden(true);
    modal.setTop();
    modal.setPortal(true);
    modal.show();
  }, [compareData, compareHeaders, hasCompareData, isRecovery, modal]);

  const renderGlpIncludeItem = (
    item: string | PlanIncludeItem,
    key: string | number,
    index: number,
  ) => {
    const labelKey = getLabel(item);
    const tagKey = getTag(item);
    const detail = getDetail(item);
    const descriptionKey = detail?.description;
    const isCgmTag = Boolean(tagKey?.endsWith('.cgm.tag'));

    return (
      <React.Fragment key={key}>
        {index > 0 && <hr className={styles.glpIncludeDivider} aria-hidden />}
        <li
          className={styles.glpIncludeItem}
          data-featured={featured ? 'true' : undefined}
          data-glp-include-row={index}
        >
          <div
            className={styles.glpIncludeTitleRow}
            data-featured={featured ? 'true' : undefined}
          >
            <span className={styles.glpIncludeTitle}>
              {translatePlan(labelKey)}
            </span>
            {tagKey ? (
              <span
                className={styles.glpIncludeTag}
                data-featured={featured ? 'true' : undefined}
              >
                {renderGlpIncludeTagLabel(translatePlan(tagKey), isCgmTag)}
              </span>
            ) : null}
          </div>
          {descriptionKey && (
            <p className={styles.glpIncludeBody}>
              {translatePlan(descriptionKey)}
            </p>
          )}
        </li>
      </React.Fragment>
    );
  };

  const renderIncludeItem = (
    item: string | PlanIncludeItem,
    key: string | number,
  ) => {
    const labelKey = getLabel(item);
    const detail = getDetail(item);
    const clickable = Boolean(detail);
    const thumbnailSrc =
      !isRecovery && detail?.thumbnailUrl ? detail.thumbnailUrl : undefined;

    return (
      <li
        className={styles.checklistItem}
        key={key}
        data-clickable={clickable ? 'true' : undefined}
        onClick={() => {
          if (detail) openIncludeDetail(labelKey, detail);
        }}
        onKeyDown={(event) => {
          if (!clickable || event.key !== 'Enter') return;
          event.preventDefault();
          if (detail) openIncludeDetail(labelKey, detail);
        }}
        tabIndex={clickable ? 0 : undefined}
        role={clickable ? 'button' : undefined}
      >
        {thumbnailSrc ? (
          <CustomImage
            className={styles.includeThumbnail}
            src={thumbnailSrc}
            alt=""
            width={28}
            height={28}
            aria-hidden
          />
        ) : (
          <span className={styles.iconWrapper} aria-hidden>
            <PlanCheckIcon />
          </span>
        )}
        <span
          className={styles.checklistLabel}
          data-clickable={clickable ? 'true' : undefined}
        >
          {t(labelKey)}
        </span>
      </li>
    );
  };

  const includes = plan.includes ?? [];
  const hasIncludes = includes.length > 0;

  const renderIncludesHeader = () => {
    if (isGlp) return null;

    if (isRecovery && plan.includesHeader) {
      return <h4 className={styles.includesHeader}>{t(plan.includesHeader)}</h4>;
    }

    if (isProPlan) {
      return (
        <h4 className={styles.includesHeader}>
          {tLab('plansSection.includesHeaderProPrefix')}{' '}
          {tLab('plansSection.includesHeaderProHighlight')}
        </h4>
      );
    }

    if (isElitePlan) {
      return (
        <h4 className={styles.includesHeader}>
          {tLab('plansSection.includesHeaderElitePrefix')}{' '}
          {tLab('plansSection.includesHeaderEliteHighlight')}
        </h4>
      );
    }

    if (plan.includesHeader) {
      return <h4 className={styles.includesHeader}>{t(plan.includesHeader)}</h4>;
    }

    return null;
  };

  const cardContent = (
    <article
      className={styles.card}
      data-featured={featured ? 'true' : undefined}
      data-in-animated-border={featured ? 'true' : undefined}
      data-glp={isGlp ? 'true' : undefined}
    >
      <div className={styles.headerBlock}>
        <div className={styles.titlePriceGroup}>
          <div className={styles.titleRow}>
            <h3 className={styles.planName}>{planName}</h3>
            {plan.tag && (
              <span className={styles.planTag}>{translatePlan(plan.tag)}</span>
            )}
          </div>
          <div className={styles.priceGroup}>
            {activeOffer ? (
              <div className={styles.priceRow}>
                <p className={styles.price}>{`₹${activeOffer.offerPrice.toLocaleString('en-IN')}`}</p>
                <p
                  className={`${styles.price} ${styles.originalPrice}`}
                >{`₹${plan.price.toLocaleString('en-IN')}`}</p>
              </div>
            ) : hasAffiliateDiscount ? (
              <div className={styles.priceRow}>
                <p className={styles.price}>{`₹${discountedPrice.toLocaleString('en-IN')}`}</p>
                <p
                  className={`${styles.price} ${styles.originalPrice}`}
                >{`₹${plan.price.toLocaleString('en-IN')}`}</p>
              </div>
            ) : (
              <p className={styles.price}>{`₹${plan.price.toLocaleString('en-IN')}`}</p>
            )}
            {isGlp && pricingTier === 'withMedication' && (
              <p className={styles.glpPricingTierLabel}>
                {tLab('glp.knowYourProgram.withMedicationLabel')}
              </p>
            )}
          </div>
        </div>
        {!isGlp && (
          <p className={styles.description}>{translatePlan(plan.description)}</p>
        )}
      </div>

      {isGlp && onSignUpClick ? (
        <button
          className={`${styles.chooseButton} pl-glp-plan-cta`}
          type="button"
          data-featured={featured ? 'true' : undefined}
          data-glp="true"
          onClick={onSignUpClick}
          aria-label={chooseLabel}
          data-buttontype={`performance_lab_glp_signup_${plan.id}`}
        >
          {chooseLabel}
        </button>
      ) : (
        <button
          className={styles.chooseButton}
          type="button"
          data-featured={featured ? 'true' : undefined}
          onClick={onChoosePlan}
          disabled={isAdding}
          aria-label={chooseLabel}
          data-buttontype={`performance_lab_landing_choose_${plan.id}`}
        >
          {isAdding ? <DotSpinner /> : chooseLabel}
        </button>
      )}

      {hasIncludes &&
        (isGlp && onViewJourney ? (
          <button
            className={`${styles.glpIncludesClickArea} pl-glp-plan-includes`}
            type="button"
            onClick={onViewJourney}
            aria-label={tLab('glp.knowYourProgram.viewJourneyAria')}
          >
            <div className={styles.includesBlock}>
              <ul className={styles.glpIncludesList}>
                {includes.map((item, idx) =>
                  renderGlpIncludeItem(item, idx, idx),
                )}
              </ul>
            </div>
          </button>
        ) : (
          <div className={styles.includesWrapper}>
            <div className={styles.includesBlock}>
              {renderIncludesHeader()}
              {isGlp ? (
                <ul className={styles.glpIncludesList}>
                  {includes.map((item, idx) =>
                    renderGlpIncludeItem(item, idx, idx),
                  )}
                </ul>
              ) : (
                <ul
                  className={styles.checklist}
                  data-spacious={!isBasicPlan ? 'true' : undefined}
                >
                  {includes.map((item, idx) => renderIncludeItem(item, idx))}
                </ul>
              )}
            </div>
          </div>
        ))}

      {isGlp && onViewJourney && (
        <div className={styles.compareFooter}>
          <hr className={styles.compareDivider} aria-hidden />
          <button
            className={`${styles.journeyFooterLink} pl-glp-plan-journey`}
            type="button"
            onClick={onViewJourney}
            aria-label={tLab('glp.knowYourProgram.viewJourneyAria')}
            data-buttontype={`performance_lab_glp_view_journey_${plan.id}`}
          >
            {tLab('glp.knowYourProgram.viewJourney')}
            <CompareArrow />
          </button>
        </div>
      )}

      {!isGlp && hasCompareData && (
        <div className={styles.compareFooter}>
          <hr className={styles.compareDivider} aria-hidden />
          <button
            className={styles.compareLink}
            type="button"
            onClick={openCompareModal}
            aria-label={
              tLab.has(`${i18nPrefix}.compareModalAria`)
                ? tLab(`${i18nPrefix}.compareModalAria`)
                : 'Compare Performance Lab plans'
            }
            data-buttontype={`performance_lab_landing_compare_${plan.id}`}
          >
            {tLab.has(`${i18nPrefix}.compareNow`)
              ? tLab(`${i18nPrefix}.compareNow`)
              : isRecovery
                ? 'Compare recovery passes'
                : 'Compare diagnostics memberships'}
            <CompareArrow />
          </button>
        </div>
      )}
    </article>
  );

  if (!featured) {
    return cardContent;
  }

  return (
    <AnimatedGradientBorder
      borderRadius={FEATURED_CARD_RADIUS_PX}
      borderWidth={1.35}
      animationDurationSeconds={8.5}
      $elevated
      $elevatedScale={!isGlp}
    >
      {cardContent}
    </AnimatedGradientBorder>
  );
};
